import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { memo, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { ButtonVariant } from "react-bootstrap/esm/types";

export interface ChecklistItem {
  id: string | number;
  label: string;
  disabled?: boolean;
}

export type SelectedItems = {
  [key: string | number]: boolean | 0 | 1;
};

type MultiSelectDropdownProps = {
  checklistItems: ChecklistItem[];
  selectedItems: SelectedItems;
  setSelectedItems: (
    selectedItems: SelectedItems,
    changedKey?: string | number
  ) => void;
  disabled?: boolean;
  placeholder?: string;
  variant?: ButtonVariant;
  forcedDropdownText?: string;
  onUnFocus?: (selectedItems: SelectedItems) => void;
};

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  checklistItems,
  selectedItems,
  setSelectedItems,
  disabled,
  variant = "light",
  placeholder,
  forcedDropdownText,
  onUnFocus,
}) => {
  const [hasChanged, setHasChanged] = useState(false);

  const getSelectedText = (selectedItems: SelectedItems) => {
    const filteredLabels: string[] = [];
    for (const [key, value] of Object.entries(selectedItems)) {
      if (!value) continue;
      const label = checklistItems.find(
        (item) => String(item.id) === String(key)
      )?.label;
      if (label) {
        filteredLabels.push(label);
      }
    }

    return filteredLabels.join(", ");
  };

  const getDropdownText = () => {
    if (forcedDropdownText) {
      return forcedDropdownText;
    }
    if (Object.keys(selectedItems).length !== 0) {
      return getSelectedText(selectedItems);
    }
    if (placeholder) {
      return placeholder;
    }
    return "Select Items";
  };

  const onChecklistItemClick = (item: ChecklistItem) => {
    if (item.disabled) return;
    setHasChanged(true);
    setSelectedItems(
      {
        ...selectedItems,
        [item.id]: !selectedItems[item.id],
      },
      item.id
    );
  };

  const getCheckOrDiv = (isChecked: boolean) =>
    isChecked ? (
      <FontAwesomeIcon icon="check" />
    ) : (
      <div style={{ width: "12px" }} />
    );

  const getDropdownItems = () =>
    checklistItems.map((item, index) => (
      <React.Fragment key={item.id}>
        <Dropdown.Item
          onClick={() => {
            onChecklistItemClick(item);
          }}
          className="row-gap-8px align-items-center"
        >
          {getCheckOrDiv(!!selectedItems[item.id])}
          <p>{item.label}</p>
        </Dropdown.Item>
        {index !== checklistItems.length - 1 && <Dropdown.Divider />}
      </React.Fragment>
    ));

  const onSaveDropdownSelectedItems = () => {
    // we should compare all of the items if no items were change then we should not update the selected items.
    if (!hasChanged) return;
    onUnFocus?.(selectedItems);
    setHasChanged(false);
  };

  // when the user selects an item we dont want to close the dropdown incase they want to select multiple items.
  return (
    <Dropdown
      autoClose="outside"
      onToggle={(isOpen) => {
        if (isOpen) return;
        onSaveDropdownSelectedItems();
      }}
    >
      <Dropdown.Toggle
        variant={variant}
        id="dropdown-basic"
        disabled={disabled}
        className="checklist-dropdown"
      >
        {getDropdownText()}
      </Dropdown.Toggle>
      <Dropdown.Menu align="end">{getDropdownItems()}</Dropdown.Menu>
    </Dropdown>
  );
};

export default memo(MultiSelectDropdown);
